import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Select, Input, Button, message } from 'antd';
import CountrySelectInput from "../shared/countrySelectInput";
import { FaCirclePlus, FaCircleMinus } from "react-icons/fa6";
import { getAlpha2Code } from "../../lib/helper/countryToAphlaCode";
import { api, API_BASE_URL } from "../../config/api";
import DhlSummaryTable from './DhlSummaryTable';
import nomHsCodes from "../../utils/nomHsCodes.json";

const { Option } = Select;

const normalizeHsCode = (value) => String(value || "").replace(/\D/g, "");

const matchesHsCodePrefixes = (items = [], prefixes = []) => {
    const normalizedPrefixes = prefixes
        .map(normalizeHsCode)
        .filter(prefix => prefix.length === 4 || prefix.length === 6);

    return items.some((item) => {
        const hsCode = normalizeHsCode(item.hsCode || item.itemHSCode);
        return normalizedPrefixes.some(prefix => hsCode.slice(0, prefix.length) === prefix);
    });
};

const getNomHsCodePrefixes = () => [
    ...(nomHsCodes.fourDigit || []),
    ...(nomHsCodes.sixDigit || [])
];

const isNomService = (service) => service?.description?.trim().toLowerCase() === "nom";
const HS_CODE_STARTS_WITH_KEYS = ["HSCode Starts with", "Shipment HS Code Starts With"];

const findHsCodeStartsWithIndex = (values = []) =>
    values.findIndex((value) => HS_CODE_STARTS_WITH_KEYS.includes(value));

// Add province/state data
const CANADIAN_PROVINCES = [
    { state: 'ALBERTA', code: 'AB' },
    { state: 'BRITISH COLUMBIA', code: 'BC' },
    { state: 'MANITOBA', code: 'MB' },
    { state: 'NEW BRUNSWICK', code: 'NB' },
    { state: 'NEWFOUNDLNAD AND LABRADOR', code: 'NL' },
    { state: 'NORTHWEST TERRITORIES', code: 'NT' },
    { state: 'NOVA SCOTIA', code: 'NS' },
    { state: 'NUNAVUT', code: 'NU' },
    { state: 'ONTARIO', code: 'ON' },
    { state: 'PRINCE EDWARD ISLAND', code: 'PE' },
    { state: 'QUEBEC', code: 'QC' },
    { state: 'SASKATCHEWAN', code: 'SK' },
    { state: 'YUKON', code: 'YT' }
];

const BRAZILIAN_STATES = [
    { state: 'ACRE', code: 'AC' },
    { state: 'ALAGOAS', code: 'AL' },
    { state: 'AMAZONAS', code: 'AM' },
    { state: 'AMAPÁ', code: 'AP' },
    { state: 'BAHIA', code: 'BA' },
    { state: 'CEARÁ', code: 'CE' },
    { state: 'DISTRITO FEDERAL', code: 'DF' },
    { state: 'ESPÍRITO SANTO', code: 'ES' },
    { state: 'GOIÁS', code: 'GO' },
    { state: 'MARANHÃO', code: 'MA' },
    { state: 'MINAS GERAIS', code: 'MG' },
    { state: 'MATO GROSSO DO SUL', code: 'MS' },
    { state: 'MATO GROSSO', code: 'MT' },
    { state: 'PARÁ', code: 'PA' },
    { state: 'PARAÍBA', code: 'PB' },
    { state: 'PERNAMBUCO', code: 'PE' },
    { state: 'PIAUÍ', code: 'PI' },
    { state: 'PARANÁ', code: 'PR' },
    { state: 'RIO DE JANEIRO', code: 'RJ' },
    { state: 'RIO GRANDE DO NORTE', code: 'RN' },
    { state: 'RONDÔNIA', code: 'RO' },
    { state: 'RORAIMA', code: 'RR' },
    { state: 'RIO GRANDE DO SUL', code: 'RS' },
    { state: 'SANTA CATARINA', code: 'SC' },
    { state: 'SERGIPE', code: 'SE' },
    { state: 'SÃO PAULO', code: 'SP' },
    { state: 'TOCANTINS', code: 'TO' }
];


const TestPopup = ({ isVisible, onClose, formData }) => {
    // Initialize state with default values
    const getInitialState = () => ({
        itemsTotalQty: 0,
        totalItemsValue: 0,
        totalItemsQuantity: 0,
        totalDimensionsWeight: 0,
        totalDimensionsLength: 0,
        totalDimensionsWidth: 0,
        totalDimensionsHeight: 0,
        localFormData: {
            ...formData,
            items: formData.items || [{ name: '', quantity: '', itemValue: '', countryOrigin: '', hsCode: '' }],
            dimensions: [{
                weight: { value: "", unit: "kg" },
                length: { value: "", unit: "cm" },
                width: { value: "", unit: "cm" },
                height: { value: "", unit: "cm" },
                quantity: ""
            }]
        },
        needInsurance: 'NO',
        estimationSummary: null,
        isEstimationComplete: false,
        dhlEstimation: null,
        accessToken: null,
        tokenExpiry: null,
        freightCharge: 0,
        insuranceCharge: 0,
        itemFreight: 0,
        itemInsurance: 0,
        transportMode: 'AIR',
        senderState: '',
        receiverState: '',
        pricingStrategy: 'EXACT',
        insurance: 0,
        airfreight: 0,
        cif: 0,
        duty: 0,
        collectionValue: 0,
        disabledDutyVatCountries: [],
        importDocsRequired: 'Not_Required',
        isDHLDataReceived: false
    });

    // State declarations
    const [itemsTotalQty, setItemsTotalQty] = useState(0);
    const [totalItemsValue, setTotalItemsValue] = useState(0);
    const [totalItemsQuantity, setTotalItemsQuantity] = useState(0); //Box Item Total Quantity
    const [totalDimensionsWeight, setTotalDimensionsWeight] = useState(0);
    const [totalDimensionsLength, setTotalDimensionsLength] = useState(0);
    const [totalDimensionsWidth, setTotalDimensionsWidth] = useState(0);
    const [totalDimensionsHeight, setTotalDimensionsHeight] = useState(0);

    const [localFormData, setLocalFormData] = useState({
        ...formData,
        items: formData.items || [{ name: '', quantity: '', itemValue: '', countryOrigin: '', hsCode: '' }],
        dimensions: [{
            weight: { value: "", unit: "kg" },
            length: { value: "", unit: "cm" },
            width: { value: "", unit: "cm" },
            height: { value: "", unit: "cm" },
            quantity: ""
        }]
    });
    const [needInsurance, setNeedInsurance] = useState('NO');
    const [estimationSummary, setEstimationSummary] = useState(null);
    const [isEstimationComplete, setIsEstimationComplete] = useState(false);

    const [dhlEstimation, setDhlEstimation] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [tokenExpiry, setTokenExpiry] = useState(null);

    const [freightCharge, setFreightCharge] = useState(0);
    const [insuranceCharge, setInsuranceCharge] = useState(0);
    const [itemFreight, setItemFreight] = useState(0);
    const [itemInsurance, setItemInsurance] = useState(0);
    const [transportMode, setTransportMode] = useState('AIR');

    const [senderState, setSenderState] = useState('');
    const [receiverState, setReceiverState] = useState('');
    const [pricingStrategy, setPricingStrategy] = useState('EXACT');

    const CIF_API = Number(totalItemsValue) + Number(freightCharge) + Number(insuranceCharge);
    // console.log("CIF_API: ", CIF_API);

    const [insurance, setInsurance] = useState(0);
    const [airfreight, setAirfreight] = useState(0);
    const [cif, setCif] = useState(0);
    const [duty, setDuty] = useState(0);
    const [collectionValue, setCollectionValue] = useState(0);
    const [disabledDutyVatCountries, setDisabledDutyVatCountries] = useState([]);

    const [importDocsRequired, setImportDocsRequired] = useState('Not_Required');

    // New state to trigger estimation summary calculation after DHL data is received
    const [isDHLDataReceived, setIsDHLDataReceived] = useState(false);

    const calculateTotalVolumetricWeight = (dimensions) => {
        return dimensions.reduce((total, dim) => {
            const length = parseFloat(dim.length.value) || 0;
            const width = parseFloat(dim.width.value) || 0;
            const height = parseFloat(dim.height.value) || 0;
            const quantity = parseFloat(dim.quantity) || 1;
            const volumetricWeightPerItem = (length * width * height) / 6000;
            return total + (volumetricWeightPerItem * quantity);
        }, 0);
    };

    // Reset all state when modal opens or closes
    const resetAllState = useCallback(() => {
        const initialState = getInitialState();
        setItemsTotalQty(initialState.itemsTotalQty);
        setTotalItemsValue(initialState.totalItemsValue);
        setTotalItemsQuantity(initialState.totalItemsQuantity);
        setTotalDimensionsWeight(initialState.totalDimensionsWeight);
        setTotalDimensionsLength(initialState.totalDimensionsLength);
        setTotalDimensionsWidth(initialState.totalDimensionsWidth);
        setTotalDimensionsHeight(initialState.totalDimensionsHeight);
        setLocalFormData(initialState.localFormData);
        setNeedInsurance(initialState.needInsurance);
        setEstimationSummary(initialState.estimationSummary);
        setIsEstimationComplete(initialState.isEstimationComplete);
        setDhlEstimation(initialState.dhlEstimation);
        setAccessToken(initialState.accessToken);
        setTokenExpiry(initialState.tokenExpiry);
        setFreightCharge(initialState.freightCharge);
        setInsuranceCharge(initialState.insuranceCharge);
        setItemFreight(initialState.itemFreight);
        setItemInsurance(initialState.itemInsurance);
        setTransportMode(initialState.transportMode);
        setSenderState(initialState.senderState);
        setReceiverState(initialState.receiverState);
        setPricingStrategy(initialState.pricingStrategy);
        setInsurance(initialState.insurance);
        setAirfreight(initialState.airfreight);
        setCif(initialState.cif);
        setDuty(initialState.duty);
        setCollectionValue(initialState.collectionValue);
        setDisabledDutyVatCountries(initialState.disabledDutyVatCountries);
        setImportDocsRequired(initialState.importDocsRequired);
        setIsDHLDataReceived(initialState.isDHLDataReceived);
    }, []);

    useEffect(() => {
        if (isVisible) {
            // Reset state when modal opens
            resetAllState();

            const fetchDisabledDutyVatCountries = async () => {
                try {
                    const response = await api.get(`${API_BASE_URL}/duty-vat/disabled`);
                    setDisabledDutyVatCountries(response.data);
                } catch (error) {
                    console.error('Error fetching disabled duty & VAT countries:', error);
                }
            };
            fetchDisabledDutyVatCountries();

            // Set fresh form data from props
            setLocalFormData({
                ...formData,
                items: formData.items || [{ name: '', quantity: '', itemValue: '', countryOrigin: '', hsCode: '' }],
                dimensions: [{
                    weight: { value: "", unit: "kg" },
                    length: { value: "", unit: "cm" },
                    width: { value: "", unit: "cm" },
                    height: { value: "", unit: "cm" },
                    quantity: ""
                }]
            });
        }
    }, [isVisible, formData, resetAllState]);

    const handleDimensionChange = (index, field, e) => {
        const { value, name } = e.target;

        setLocalFormData(prevData => {
            const newDimensions = [...prevData.dimensions];

            if (field === 'quantity') {
                // Handle quantity field directly
                newDimensions[index].quantity = value;
            } else if (field === 'weight' && name && name.includes('unit')) {
                newDimensions.forEach(dim => {
                    dim.weight.unit = value;
                });
            } else if (name && name.includes('unit')) {
                newDimensions[index][field].unit = value;
            } else {
                newDimensions[index][field].value = value;
            }

            const totalWeight = newDimensions.reduce((sum, dim) => {
                const weight = parseFloat(dim.weight.value || 0);
                const quantity = parseInt(dim.quantity || 0);
                return sum + (weight * quantity);
            }, 0);
            const totalLength = newDimensions.reduce((sum, dim) => sum + parseFloat(dim.length.value || 0), 0);
            const totalWidth = newDimensions.reduce((sum, dim) => sum + parseFloat(dim.width.value || 0), 0);
            const totalHeight = newDimensions.reduce((sum, dim) => sum + parseFloat(dim.height.value || 0), 0);

            setTotalDimensionsWeight(totalWeight);
            setTotalDimensionsLength(totalLength);
            setTotalDimensionsWidth(totalWidth);
            setTotalDimensionsHeight(totalHeight);

            return {
                ...prevData,
                dimensions: newDimensions
            };
        });
    };

    const handleAddDimension = () => {
        setLocalFormData(prevData => ({
            ...prevData,
            dimensions: [
                ...prevData.dimensions,
                {
                    weight: { value: "", unit: prevData.dimensions[0].weight.unit },
                    length: { value: "", unit: "cm" },
                    width: { value: "", unit: "cm" },
                    height: { value: "", unit: "cm" },
                    quantity: ""
                }
            ]
        }));
    };

    const handleRemoveDimension = (index) => {
        if (localFormData.dimensions.length > 1) {
            setLocalFormData(prevData => ({
                ...prevData,
                dimensions: prevData.dimensions.filter((_, i) => i !== index)
            }));
        }
    };

    // Calculate totals
    useEffect(() => {
        const total = localFormData.items.reduce((sum, item) => {
            const value = parseFloat(item.itemValue) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            return sum + (value * quantity);
        }, 0);

        const totalQuantity = localFormData.items.reduce((sum, item) => {
            const quantity = parseFloat(item.quantity) || 0;
            return sum + quantity;
        }, 0);

        setTotalItemsValue(total);
        setItemsTotalQty(totalQuantity);

    }, [localFormData.items]);

    useEffect(() => {
        const calculateTotalQuantity = () => {
            const total = localFormData.dimensions.reduce((sum, item) => {
                return sum + (parseFloat(item.quantity) || 0);
            }, 0);
            // console.log("total", total);
            setTotalItemsQuantity(total);
        };

        calculateTotalQuantity();
    }, [localFormData.dimensions]);

    useEffect(() => {
        const calculateAirfreightCost = () => {
            const totalVolWeight = calculateTotalVolumetricWeight(localFormData.dimensions);
            const chargeableWeight = Math.max(totalVolWeight, totalDimensionsWeight);

            // console.log('Calculating Airfreight Cost:', {
            //     totalVolWeight,
            //     totalDimensionsWeight,
            //     chargeableWeight
            // });

            let airfreightCost;
            if (chargeableWeight < 360) {
                airfreightCost = 6.50 * chargeableWeight + 250;
            } else if (chargeableWeight >= 360 && chargeableWeight <= 1080) {
                airfreightCost = 5.00 * chargeableWeight + 250;
            } else {
                airfreightCost = 4.00 * chargeableWeight + 250;
            }
            // console.log(airfreightCost, "airfreightCost");

            setAirfreight(airfreightCost);
        };

        calculateAirfreightCost();
    }, [localFormData.dimensions, totalDimensionsWeight]);

    useEffect(() => {
        const calculateInsurance = () => {
            const calculatedInsurance = needInsurance === 'YES' ? totalItemsValue * 0.0075 : 0;
            setInsurance(calculatedInsurance);
        };

        calculateInsurance();
    }, [needInsurance, totalItemsValue]);

    useEffect(() => {
        const calculateCIF = () => {
            const calculatedCIF = totalItemsValue + airfreight + insurance + duty + collectionValue;
            setCif(calculatedCIF);
        };

        calculateCIF();
    }, [totalItemsValue, airfreight, insurance, duty, collectionValue]);

    const handleAddItem = () => {
        setLocalFormData(prevData => ({
            ...prevData,
            items: [...prevData.items, { name: '', quantity: '', itemValue: '', countryOrigin: '', hsCode: '' }]
        }));
    };

    const handleRemoveItem = (index) => {
        setLocalFormData(prevData => ({
            ...prevData,
            items: prevData.items.filter((_, i) => i !== index)
        }));
    };

    const handleItemChange = (index, field, value) => {
        setLocalFormData(prevData => {
            const newItems = [...prevData.items];
            newItems[index] = { ...newItems[index], [field]: value };
            return { ...prevData, items: newItems };
        });
    };

    // Create a separate, reusable function for getting unique HSCode count
    const getUniqueHSCodeCount = (items) => {
        const uniqueHSCodes = new Set(
            items.map((item) => item.itemHSCode || item.hsCode).filter(Boolean)
        );
        return uniqueHSCodes.size;
    };

    const evaluateSimpleCondition = (formula, values, calculatedValues) => {
        // console.log('Starting evaluateSimpleCondition with:', {
        //     formula,
        //     values: JSON.stringify(values),
        //     calculatedValues: JSON.stringify(calculatedValues)
        // });

        const getDynamicValue = (valueKey) => {
            switch (valueKey.toLowerCase()) {
                case "document":
                    return importDocsRequired === 'Required' ? true : false;
                case "shipment value":
                case "invoice value":
                    return totalItemsValue;
                case "quantity":
                    return totalItemsQuantity;
                case "shipment weight":
                case "item weight":
                    return totalDimensionsWeight;
                case "total item quantity":
                    return itemsTotalQty;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                case "Airfreight Volume":
                    return calculateTotalVolumetricWeight(values.dimensions);
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        return 6.50 * volumeWeight + 250;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        return 5.00 * volumeWeight + 250;
                    } else {
                        return 4.00 * volumeWeight + 250;
                    }
                case "cif":
                    return cif;
                case "cif110":
                    return totalItemsValue * 1.10;
                case "insurance":
                    return insurance;
                case "hscode count":
                    return getUniqueHSCodeCount(values.items);
                case "ior":
                    return importDocsRequired === 'Required' ? Math.max((totalItemsValue * 7.5) / 100, 950) : Math.max((totalItemsValue * 5) / 100, 650);
                case "handling charges":
                    const totalVolWeightHCLC = calculateTotalVolumetricWeight(values.dimensions);
                    const chargeableWeightHCLC = Math.max(totalDimensionsWeight, totalVolWeightHCLC);
                    return chargeableWeightHCLC >= 360 ? 250 : 200;
                case "administration":
                    if (totalItemsValue < 800) return 150;
                    if (totalItemsValue <= 10000) return 200;
                    return 250;
                case "customs clearance a class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 2500) return 350;
                    if (totalItemsValue < 100000) return 450;
                    return 550;
                case "handling a class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 300;
                    if (totalItemsValue < 500000) return 350;
                    return 450;
                case "administration a class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 2500) return 150;
                    if (totalItemsValue < 500000) return 250;
                    return 300;
                case "customs clearance b class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 1000) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 650;
                case "thc b class":
                    if (totalItemsValue < 1000) return 195;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 395;
                    if (totalItemsValue < 500000) return 450;
                    return 550;
                case "handling b class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 250;
                    return 350;
                case "administration b class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 50000) return 200;
                    return 250;
                default:
                    return calculatedValues[valueKey] !== undefined ?
                        calculatedValues[valueKey] :
                        parseFloat(valueKey) || 0;
            }
        };

        let index = 0;
        while (index < formula.length) {
            if (formula[index] === 'if') {
                index++;

                // Handle condition based on type
                const conditionType = formula[index].toLowerCase();

                // Boolean condition check (like 'document')
                if (['document'].includes(conditionType)) {
                    const conditionValue = getDynamicValue(conditionType);
                    index++;

                    // Find 'then' keyword
                    while (index < formula.length && formula[index] !== 'then') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'then') {
                        index++; // Move past 'then'

                        // Get the value after 'then'
                        const thenValue = getDynamicValue(formula[index]);

                        // If condition is true, return the 'then' value
                        if (conditionValue === true) {
                            // console.log(`Condition '${conditionType}' is true, returning ${thenValue}`);
                            return thenValue;
                        }

                        // Move index to look for 'else'
                        while (index < formula.length && formula[index] !== 'else') {
                            index++;
                        }

                        if (index < formula.length && formula[index] === 'else') {
                            index++; // Move past 'else'

                            // Skip optional 'then' after 'else'
                            if (index < formula.length && formula[index] === 'then') {
                                index++;
                            }

                            // Get the value after 'else' or 'else then'
                            const elseValue = getDynamicValue(formula[index]);

                            // If condition is false, return the 'else' value
                            if (conditionValue === false) {
                                // console.log(`Condition '${conditionType}' is false, returning ${elseValue}`);
                                return elseValue;
                            }
                        }
                    }
                }
                // Numeric comparison conditions (like value > number)
                else {
                    const leftValue = getDynamicValue(formula[index]);
                    index++;

                    if (index < formula.length && ['>', '<', '>=', '<=', '=='].includes(formula[index])) {
                        const operator = formula[index];
                        index++;
                        const rightValue = getDynamicValue(formula[index]);
                        index++;

                        let conditionMet = false;
                        switch (operator) {
                            case '>': conditionMet = leftValue > rightValue; break;
                            case '<': conditionMet = leftValue < rightValue; break;
                            case '>=': conditionMet = leftValue >= rightValue; break;
                            case '<=': conditionMet = leftValue <= rightValue; break;
                            case '==': conditionMet = leftValue === rightValue; break;
                        }

                        // console.log(`Condition: ${leftValue} ${operator} ${rightValue}, Result: ${conditionMet}`);

                        // Find 'then' token
                        while (index < formula.length && formula[index] !== 'then') {
                            index++;
                        }

                        if (index < formula.length && formula[index] === 'then') {
                            index++; // Move past 'then'

                            // Get the value after 'then'
                            const thenValue = getDynamicValue(formula[index]);

                            // If condition is met, return the 'then' value
                            if (conditionMet) {
                                // console.log(`Numeric condition met, returning ${thenValue}`);
                                return thenValue;
                            }

                            // Move index to look for 'else'
                            while (index < formula.length && formula[index] !== 'else') {
                                index++;
                            }

                            if (index < formula.length && formula[index] === 'else') {
                                index++; // Move past 'else'

                                // Skip optional 'then' after 'else'
                                if (index < formula.length && formula[index] === 'then') {
                                    index++;
                                }

                                // Get the value after 'else' or 'else then'
                                const elseValue = getDynamicValue(formula[index]);

                                // If condition is not met, return the 'else' value
                                if (!conditionMet) {
                                    // console.log(`Numeric condition not met, returning ${elseValue}`);
                                    return elseValue;
                                }
                            }
                        }
                    }
                }
            }
            index++;
        }

        // Default return if no matching condition found
        // console.log('No matching condition found, returning 0');
        return 0;
    };

    const evaluateConditionalFormula = (formula, values, calculatedValues) => {
        // console.log('Starting evaluateConditionalFormula with:', {
        //     formula,
        //     values: JSON.stringify(values),
        //     calculatedValues: JSON.stringify(calculatedValues)
        // });

        const getDynamicValue = (valueKey) => {
            switch (valueKey.toLowerCase()) {
                case "shipment value":
                case "invoice value":
                    return totalItemsValue;
                case "quantity":
                    return totalItemsQuantity;
                case "shipment weight":
                case "item weight":
                    return totalDimensionsWeight;
                case "total item quantity":
                    return itemsTotalQty;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                case "Airfreight Volume":
                    return calculateTotalVolumetricWeight(values.dimensions);
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        return 6.50 * volumeWeight + 250;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        return 5.00 * volumeWeight + 250;
                    } else {
                        return 4.00 * volumeWeight + 250;
                    }
                case "cif":
                    return cif;
                case "cif110":
                    return totalItemsValue * 1.10;
                case "insurance":
                    return insurance;
                case "hscode count":
                    return getUniqueHSCodeCount(values.items);
                case "ior":
                    return importDocsRequired === 'Required' ? Math.max((totalItemsValue * 7.5) / 100, 950) : Math.max((totalItemsValue * 5) / 100, 650);
                case "handling charges":
                    const totalVolWeightHCLC = calculateTotalVolumetricWeight(values.dimensions);
                    const chargeableWeightHCLC = Math.max(totalDimensionsWeight, totalVolWeightHCLC);
                    return chargeableWeightHCLC >= 360 ? 250 : 200;
                case "administration":
                    if (totalItemsValue < 800) return 150;
                    if (totalItemsValue <= 10000) return 200;
                    return 250;
                case "customs clearance a class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 2500) return 350;
                    if (totalItemsValue < 100000) return 450;
                    return 550;
                case "handling a class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 300;
                    if (totalItemsValue < 500000) return 350;
                    return 450;
                case "administration a class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 2500) return 150;
                    if (totalItemsValue < 500000) return 250;
                    return 300;
                case "customs clearance b class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 1000) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 650;
                case "thc b class":
                    if (totalItemsValue < 1000) return 195;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 395;
                    if (totalItemsValue < 500000) return 450;
                    return 550;
                case "handling b class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 250;
                    return 350;
                case "administration b class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 50000) return 200;
                    return 250;
                case "document":
                    return importDocsRequired === 'Required' ? true : false;
                default:
                    return calculatedValues[valueKey] !== undefined ?
                        calculatedValues[valueKey] :
                        parseFloat(valueKey) || 0;
            }
        };

        // Process max/min function with comma-separated expressions
        const evaluateMinMax = (func, expressions) => {
            // console.log(`Evaluating ${func} with expressions:`, expressions);
            const results = expressions.map(expr => {
                const tokens = expr.trim().split(' ').filter(t => t);
                let result = 0;
                let operator = '+';

                for (let i = 0; i < tokens.length; i++) {
                    if (['+', '-', '*', '/', '%'].includes(tokens[i])) {
                        operator = tokens[i];
                    } else {
                        const value = getDynamicValue(tokens[i]);

                        switch (operator) {
                            case '+': result += value; break;
                            case '-': result -= value; break;
                            case '*': result *= value; break;
                            case '/': result /= value !== 0 ? value : 1; break;
                            case '%': result = (result * value) / 100; break;
                        }
                    }
                }
                return result;
            });

            return func === 'min' ? Math.min(...results) : Math.max(...results);
        };

        // Find if/else-if/else blocks
        let index = 0;
        while (index < formula.length) {
            if (formula[index] === 'if') {
                // Parse condition
                index++;
                const conditionType = formula[index].toLowerCase();

                // Handle document type condition
                if (conditionType === 'document') {
                    const isDocument = getDynamicValue('document');
                    index++;

                    // Find 'then' token
                    while (index < formula.length && formula[index] !== 'then') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'then') {
                        index++;
                        // Handle then case - find function (max/min)
                        if (['max', 'min'].includes(formula[index])) {
                            const func = formula[index];
                            index++;

                            // Find opening parenthesis
                            while (index < formula.length && formula[index] !== '(') {
                                index++;
                            }
                            index++; // Skip '('

                            // Collect expressions until closing parenthesis
                            const expressions = [];
                            let currentExpr = '';
                            let parenCount = 1;

                            while (index < formula.length && parenCount > 0) {
                                if (formula[index] === '(') parenCount++;
                                else if (formula[index] === ')') parenCount--;

                                if (formula[index] === ',' && parenCount === 1) {
                                    expressions.push(currentExpr);
                                    currentExpr = '';
                                } else if (parenCount > 0) {
                                    currentExpr += formula[index] + ' ';
                                }
                                index++;
                            }
                            if (currentExpr) expressions.push(currentExpr);

                            if (isDocument) {
                                return evaluateMinMax(func, expressions);
                            }
                        }
                    }

                    // If condition false, find 'else' token
                    while (index < formula.length && formula[index] !== 'else') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'else') {
                        index++;
                        // Skip 'then' if it exists after else
                        if (index < formula.length && formula[index] === 'then') {
                            index++;
                        }

                        // Handle else case
                        if (['max', 'min'].includes(formula[index])) {
                            const func = formula[index];
                            index++;

                            // Find opening parenthesis
                            while (index < formula.length && formula[index] !== '(') {
                                index++;
                            }
                            index++; // Skip '('

                            // Collect expressions until closing parenthesis
                            const expressions = [];
                            let currentExpr = '';
                            let parenCount = 1;

                            while (index < formula.length && parenCount > 0) {
                                if (formula[index] === '(') parenCount++;
                                else if (formula[index] === ')') parenCount--;

                                if (formula[index] === ',' && parenCount === 1) {
                                    expressions.push(currentExpr);
                                    currentExpr = '';
                                } else if (parenCount > 0) {
                                    currentExpr += formula[index] + ' ';
                                }
                                index++;
                            }
                            if (currentExpr) expressions.push(currentExpr);

                            if (!isDocument) {
                                return evaluateMinMax(func, expressions);
                            }
                        }
                    }
                }
                // Handle numeric comparison conditions (e.g., 'Invoice Value' > 5000)
                else {
                    const leftValue = getDynamicValue(formula[index]);
                    index++;
                    const operator = formula[index];
                    index++;
                    const rightValue = getDynamicValue(formula[index]);
                    index++;

                    let conditionMet = false;
                    switch (operator) {
                        case '>': conditionMet = leftValue > rightValue; break;
                        case '<': conditionMet = leftValue < rightValue; break;
                        case '>=': conditionMet = leftValue >= rightValue; break;
                        case '<=': conditionMet = leftValue <= rightValue; break;
                        case '==': conditionMet = leftValue === rightValue; break;
                    }

                    // console.log(`Condition: ${formula[index - 3]} ${operator} ${formula[index - 1]}, Result: ${conditionMet}`);

                    // Find 'then' token
                    while (index < formula.length && formula[index] !== 'then') {
                        index++;
                    }

                    if (index < formula.length && formula[index] === 'then') {
                        index++;
                        // Handle then case - find function (max/min)
                        if (['max', 'min'].includes(formula[index])) {
                            const func = formula[index];
                            index++;

                            // Find opening parenthesis
                            while (index < formula.length && formula[index] !== '(') {
                                index++;
                            }
                            index++; // Skip '('

                            // Collect expressions until closing parenthesis
                            const expressions = [];
                            let currentExpr = '';
                            let parenCount = 1;

                            while (index < formula.length && parenCount > 0) {
                                if (formula[index] === '(') parenCount++;
                                else if (formula[index] === ')') parenCount--;

                                if (formula[index] === ',' && parenCount === 1) {
                                    expressions.push(currentExpr);
                                    currentExpr = '';
                                } else if (parenCount > 0) {
                                    currentExpr += formula[index] + ' ';
                                }
                                index++;
                            }
                            if (currentExpr) expressions.push(currentExpr);

                            if (conditionMet) {
                                return evaluateMinMax(func, expressions);
                            }
                        }
                    }

                    // If condition false, find else-if or else token
                    while (index < formula.length &&
                        formula[index] !== 'else if' &&
                        formula[index] !== 'else') {
                        index++;
                    }

                    if (index < formula.length) {
                        if (formula[index] === 'else if') {
                            // Handle else-if case (recursive)
                            const remainingFormula = formula.slice(index);
                            return evaluateConditionalFormula(remainingFormula, values, calculatedValues);
                        } else if (formula[index] === 'else') {
                            index++;
                            // Skip 'then' if it exists after else
                            if (index < formula.length && formula[index] === 'then') {
                                index++;
                            }

                            // Handle else case
                            if (['max', 'min'].includes(formula[index])) {
                                const func = formula[index];
                                index++;

                                // Find opening parenthesis
                                while (index < formula.length && formula[index] !== '(') {
                                    index++;
                                }
                                index++; // Skip '('

                                // Collect expressions until closing parenthesis
                                const expressions = [];
                                let currentExpr = '';
                                let parenCount = 1;

                                while (index < formula.length && parenCount > 0) {
                                    if (formula[index] === '(') parenCount++;
                                    else if (formula[index] === ')') parenCount--;

                                    if (formula[index] === ',' && parenCount === 1) {
                                        expressions.push(currentExpr);
                                        currentExpr = '';
                                    } else if (parenCount > 0) {
                                        currentExpr += formula[index] + ' ';
                                    }
                                    index++;
                                }
                                if (currentExpr) expressions.push(currentExpr);

                                if (!conditionMet) {
                                    return evaluateMinMax(func, expressions);
                                }
                            }
                        }
                    }
                }
            }
            index++;
        }

        // Default return if no matching condition found
        return 0;
    };

    const evaluateComplexFormula = (formula, values, calculatedValues) => {

        // console.log('Starting evaluateComplexFormula with:', {
        //     formula,
        //     values: JSON.stringify(values),
        //     calculatedValues: JSON.stringify(calculatedValues)
        // });

        const tokens = formula.flat();  // Flatten the array in case it's nested

        // console.log('Flattened tokens:', tokens);

        const precedence = {
            '*': 2,
            '/': 2,
            '+': 1,
            '-': 1,
            '%': 2
        };

        const applyOp = (op, b, a) => {
            switch (op) {
                case '+': return a + b;
                case '-': return a - b;
                case '*': return a * b;
                case '/': return b !== 0 ? a / b : Infinity;
                case '%': return (a * b) / 100;
            }
        };

        const getDynamicValue = (valueKey) => {
            switch (valueKey) {
                case "shipment value":
                case "Invoice Value":
                case "Shipment Value":
                    return totalItemsValue;
                case "Quantity":
                    return totalItemsQuantity;
                case "shipment weight":
                case "Item Weight":
                    return totalDimensionsWeight;
                case "Total Item Quantity":
                    return itemsTotalQty;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                case "Airfreight Volume":
                    return calculateTotalVolumetricWeight(values.dimensions);
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        return 6.50 * volumeWeight + 250;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        return 5.00 * volumeWeight + 250;
                    } else {
                        return 4.00 * volumeWeight + 250;
                    }
                case "CIF":
                    return cif;
                case "CIF110":
                    return totalItemsValue * 1.10;
                case "Insurance":
                    return insurance;
                case "HSCode Count":
                    return getUniqueHSCodeCount(values.items);
                case "Customs Clearance A Class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 2500) return 350;
                    if (totalItemsValue < 100000) return 450;
                    return 550;
                case "Handling A Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 300;
                    if (totalItemsValue < 500000) return 350;
                    return 450;
                case "Administration A Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 2500) return 150;
                    if (totalItemsValue < 500000) return 250;
                    return 300;
                case "THC A Class":
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 195;
                    if (totalItemsValue < 50000) return 350;
                    if (totalItemsValue < 100000) return 400;
                    if (totalItemsValue < 500000) return 450;
                    return 575;
                case "Customs Clearance B Class":
                    if (totalItemsValue < 250) return 250;
                    if (totalItemsValue < 1000) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 650;
                case "THC B Class":
                    if (totalItemsValue < 1000) return 195;
                    if (totalItemsValue < 2500) return 250;
                    if (totalItemsValue < 50000) return 395;
                    if (totalItemsValue < 500000) return 450;
                    return 550;
                case "Handling B Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 250;
                    return 350;
                case "Administration B Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 100;
                    if (totalItemsValue < 50000) return 200;
                    // covers < 2500 too
                    return 250;
                case "Customs Clearance C Class":
                    if (totalItemsValue < 250) return 350;
                    if (totalItemsValue < 2500) return 450;
                    if (totalItemsValue < 100000) return 550;
                    return 750;
                case "Handling C Class":
                    if (totalItemsValue < 250) return 75;
                    if (totalItemsValue < 1000) return 200;
                    if (totalItemsValue < 50000) return 250;
                    if (totalItemsValue < 100000) return 350;
                    return 550;
                case "Administration C Class":
                    if (totalItemsValue < 250) return 50;
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 50000) return 200;
                    return 250;
                case "THC C Class":
                    if (totalItemsValue < 1000) return 150;
                    if (totalItemsValue < 2500) return 195;
                    if (totalItemsValue < 50000) return 350;
                    if (totalItemsValue < 100000) return 400;
                    if (totalItemsValue < 500000) return 450;
                    return 575;
                case "document":
                    return importDocsRequired === 'Required' ? true : false;
                default:
                    return calculatedValues[valueKey] !== undefined ? calculatedValues[valueKey] : parseFloat(valueKey) || 0;
            }
        };

        const evaluate = () => {
            const values = [];
            const ops = [];

            // console.log('Starting evaluation of tokens');

            for (let i = 0; i < tokens.length; i++) {

                // console.log(`Processing token: ${tokens[i]}`);

                if (tokens[i] === '(') {
                    // console.log('Pushing opening parenthesis');
                    ops.push(tokens[i]);
                }
                else if (tokens[i] === ')') {
                    // console.log('Closing parenthesis encountered');
                    while (ops.length && ops[ops.length - 1] !== '(') {
                        let op = ops.pop();
                        let b = values.pop();
                        let a = values.pop();
                        values.push(applyOp(op, b, a));
                    }
                    ops.pop(); // Remove '('
                    // console.log('Closed parenthesis block');
                }
                else if (['+', '-', '*', '/', '%'].includes(tokens[i])) {
                    // console.log(`Operator found: ${tokens[i]}`);
                    while (ops.length && precedence[ops[ops.length - 1]] >= precedence[tokens[i]]) {
                        let op = ops.pop();
                        let b = values.pop();
                        let a = values.pop();
                        values.push(applyOp(op, b, a));
                    }
                    ops.push(tokens[i]);
                }
                else {
                    values.push(getDynamicValue(tokens[i]));
                }
                // console.log('Current values stack:', values);
                // console.log('Current operators stack:', ops);
            }

            // console.log('Final evaluation of remaining operators');
            while (ops.length) {
                let op = ops.pop();
                let b = values.pop();
                let a = values.pop();
                values.push(applyOp(op, b, a));
                // console.log('After final operation - values:', values);
            }

            // console.log('Final result:', values[0]);
            return values[0];
        };

        return evaluate();
    };

    const calculateServiceValue = (service, values, calculatedValues) => {

        // console.log('Starting calculateServiceValue with:', {
        //     service: JSON.stringify(service),
        //     values: JSON.stringify(values),
        //     calculatedValues: JSON.stringify(calculatedValues)
        // });

        const nomHsCodeConditionIndex = isNomService(service) ? findHsCodeStartsWithIndex(service.values) : -1;

        if (nomHsCodeConditionIndex !== -1) {
            const hsCodeMatches = matchesHsCodePrefixes(values.items, getNomHsCodePrefixes());
            const thenIndex = service.values.indexOf("then", nomHsCodeConditionIndex);
            const elseIndex = service.values.indexOf("else", thenIndex);
            const formulaStartIndex = hsCodeMatches ? thenIndex + 1 : elseIndex + 1;
            const formulaEndIndex = hsCodeMatches && elseIndex !== -1 ? elseIndex : service.values.length;

            if (formulaStartIndex > 0) {
                return calculateServiceValue(
                    { values: service.values.slice(formulaStartIndex, formulaEndIndex) },
                    values,
                    calculatedValues
                );
            }

            return 0;
        }

        if (service.values.includes('if') &&
            (service.values.includes('max') || service.values.includes('min')) &&
            (service.values.includes('then') || service.values.includes('else'))) {
            return evaluateConditionalFormula(service.values, values, calculatedValues);
        }
        // Add this check in the calculateServiceValue function, after your existing checks for other formula types
        if (service.values.includes('if') && service.values.includes('document') && !service.values.includes('max') && !service.values.includes('min')) {
            // This seems to be a simple condition with direct value assignment
            return evaluateSimpleCondition(service.values, values, calculatedValues);
        }

        if (!service.values || service.values.length === 0) return 0;
        if (service.description === 'Insurance' && values.insuranceSelected !== 'YES') {
            return 0;
        }
        let result = 0;
        let operator = "+";
        let lastValue = 0;

        // Formulae Step 5 start:

        const evaluateComplexCondition = (formulaSlice) => {
            let index = 0;

            const getValue = (key) => {
                switch (key) {
                    case 'shipment value':
                    case 'Invoice Value':
                    case 'Shipment Value':
                        return totalItemsValue;
                    case 'shipment weight':
                    case 'Item Weight':
                        return totalDimensionsWeight;
                    case "Quantity":
                        return totalItemsQuantity;
                    case "Total Item Quantity":
                        return itemsTotalQty;
                    case "Volume Weight":
                        const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                        // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                        return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                    case "Airfreight Volume":
                        return calculateTotalVolumetricWeight(values.dimensions);
                    case "Airfreight Charges":
                        const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                        const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                        if (volumeWeight < 360) {
                            return 6.50 * volumeWeight + 250;
                        } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                            return 5.00 * volumeWeight + 250;
                        } else {
                            return 4.00 * volumeWeight + 250;
                        }
                    case "CIF":
                        return cif;
                    case "CIF110":
                        return totalItemsValue * 1.10;
                    case "Insurance":
                        return insurance;
                    case "HSCode Count":
                        return getUniqueHSCodeCount(values.items);
                    case "IOR":
                        const isDocRequiredTC = importDocsRequired === 'Required';
                        if (isDocRequiredTC) return Math.max((totalItemsValue * 7.5) / 100, 950);
                        return Math.max((totalItemsValue * 5) / 100, 650);
                    case "Handling Charges":
                        const totalVolWeightHCTC = calculateTotalVolumetricWeight(values.dimensions);
                        const chargeableWeightHCTC = Math.max(totalDimensionsWeight, totalVolWeightHCTC);
                        return chargeableWeightHCTC >= 360 ? 250 : 200;
                    case "Administration":
                        if (totalItemsValue < 800) return 150;
                        if (totalItemsValue <= 10000) return 200;
                        return 250;
                    case "Customs Clearance A Class":
                        if (totalItemsValue < 250) return 250;
                        if (totalItemsValue < 2500) return 350;
                        if (totalItemsValue < 100000) return 450;
                        return 550;
                    case "Handling A Class":
                        if (totalItemsValue < 250) return 75;
                        if (totalItemsValue < 1000) return 150;
                        if (totalItemsValue < 2500) return 250;
                        if (totalItemsValue < 50000) return 300;
                        if (totalItemsValue < 500000) return 350;
                        return 450;
                    case "Administration A Class":
                        if (totalItemsValue < 250) return 50;
                        if (totalItemsValue < 1000) return 100;
                        if (totalItemsValue < 2500) return 150;
                        if (totalItemsValue < 500000) return 250;
                        return 300;
                    case "Customs Clearance B Class":
                        if (totalItemsValue < 250) return 250;
                        if (totalItemsValue < 1000) return 350;
                        if (totalItemsValue < 2500) return 450;
                        if (totalItemsValue < 100000) return 550;
                        return 650;
                    case "THC B Class":
                        if (totalItemsValue < 1000) return 195;
                        if (totalItemsValue < 2500) return 250;
                        if (totalItemsValue < 50000) return 395;
                        if (totalItemsValue < 500000) return 450;
                        return 550;
                    case "Handling B Class":
                        if (totalItemsValue < 250) return 75;
                        if (totalItemsValue < 1000) return 150;
                        if (totalItemsValue < 50000) return 250;
                        return 350;
                    case "Administration B Class":
                        if (totalItemsValue < 250) return 50;
                        if (totalItemsValue < 1000) return 100;
                        if (totalItemsValue < 50000) return 200;
                        // covers < 2500 too
                        return 250;
                    case "document":
                        return importDocsRequired === 'Required' ? true : false;
                    default:
                        return calculatedValues[key] !== undefined ? calculatedValues[key] : parseFloat(key) || 0;
                }
            };

            const evaluateCondition = (value1, operator, value2) => {
                switch (operator) {
                    case '>': return value1 > value2;
                    case '<': return value1 < value2;
                    case '>=': return value1 >= value2;
                    case '<=': return value1 <= value2;
                    case '==': return value1 === value2;
                    default: return false;
                }
            };

            const evaluateExpression = (startIndex) => {
                let result = getValue(formulaSlice[startIndex]);
                let i = startIndex + 1;

                while (i < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[i])) {
                    const operator = formulaSlice[i];
                    i++;
                    const value = getValue(formulaSlice[i]);
                    i++;

                    switch (operator) {
                        case '+': result += value; break;
                        case '-': result -= value; break;
                        case '*': result *= value; break;
                        case '/': result /= value !== 0 ? value : 1; break;
                        case '%': result = (result * value) / 100; break;
                    }
                }

                return result;
            };

            while (index < formulaSlice.length) {
                if (formulaSlice[index] === 'if' || formulaSlice[index] === 'else if') {
                    index++;
                    const conditionType = formulaSlice[index];

                    if (HS_CODE_STARTS_WITH_KEYS.includes(conditionType) || conditionType === 'HSCode') {
                        const prefixes = [];
                        index++;

                        if (formulaSlice[index] === 'Starts with') {
                            index++;
                        }

                        while (index < formulaSlice.length && formulaSlice[index] !== 'then') {
                            if (formulaSlice[index] !== ',') {
                                prefixes.push(formulaSlice[index]);
                            }
                            index++;
                        }

                        const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);

                        if (formulaSlice[index] === 'then') {
                            index++;

                            if (hsCodeMatches) {
                                return evaluateExpression(index);
                            }

                            while (index < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[index])) {
                                index++;
                            }
                        }

                        continue;
                    }

                    const value1 = getValue(conditionType);
                    index++;
                    const operator = formulaSlice[index];
                    index++;
                    const value2 = getValue(formulaSlice[index]);
                    index++;

                    if (evaluateCondition(value1, operator, value2)) {
                        index++; // Skip 'then'
                        return evaluateExpression(index);
                    } else {
                        // Skip to next 'else if' or 'else'
                        while (index < formulaSlice.length && !['else', 'else if'].includes(formulaSlice[index])) {
                            index++;
                        }
                    }
                } else if (formulaSlice[index] === 'else') {
                    index += 2; // Skip 'else' and 'then'
                    return evaluateExpression(index);
                } else {
                    index++;
                }
            }

            return null;
        };

        // Ending Step 5 Formulae.

        const checkHSCodeStartsWith = (items, prefixes) => {
            const prefixArray = service.description?.trim().toLowerCase() === "nom"
                ? getNomHsCodePrefixes()
                : (Array.isArray(prefixes) ? prefixes : [prefixes]);

            return matchesHsCodePrefixes(items, prefixArray);
        };


        const getDynamicValue = (valueKey) => {
            switch (valueKey) {
                case "Invoice Value":
                    return totalItemsValue;

                case "Item Weight":
                    return totalDimensionsWeight;
                case "Volume Weight":
                    const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                    // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                    return Math.max(totalDimensionsWeight, totalVolumetricWeight);
                case "Airfreight Volume":
                    return calculateTotalVolumetricWeight(values.dimensions);
                case "Airfreight Charges":
                    const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                    const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                    if (volumeWeight < 360) {
                        return 6.50 * volumeWeight + 250;
                    } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                        return 5.00 * volumeWeight + 250;
                    } else {
                        return 4.00 * volumeWeight + 250;
                    }
                case "CIF":
                    return cif;
                case "CIF110":
                    return totalItemsValue * 1.10;
                case "Insurance":
                    return insurance;
                case "Quantity":
                    return totalItemsQuantity;
                case "Total Item Quantity":
                    return itemsTotalQty;
                case "HSCode Count":
                    return getUniqueHSCodeCount(values.items);
                default:
                    // Check if it's a reference to another service
                    if (calculatedValues[valueKey] !== undefined) {
                        return calculatedValues[valueKey];
                    }
                    // If it's not a special keyword, treat it as a numeric value
                    return parseFloat(valueKey) || 0;
            }
        };


        const evaluateIfCondition = (values, index) => {
            // console.log('Evaluating if condition');
            const value1Key = values[index + 1];
            const operator = values[index + 2];
            const value2Key = values[index + 3];
            const thenKeyword = values[index + 4];
            const thenValue = values[index + 5];

            const value1 = getDynamicValue(value1Key);
            const value2 = getDynamicValue(value2Key);

            // console.log(`Condition: ${value1Key}(${value1}) ${operator} ${value2Key}(${value2})`);

            let conditionMet = false;
            switch (operator) {
                case ">":
                    conditionMet = value1 > value2;
                    break;
                case "<":
                    conditionMet = value1 < value2;
                    break;
                case ">=":
                    conditionMet = value1 >= value2;
                    break;
                case "<=":
                    conditionMet = value1 <= value2;
                    break;
                case "==":
                    conditionMet = value1 === value2;
                    break;
            }

            // console.log(`Condition met: ${conditionMet}`);

            if (conditionMet && thenKeyword === "then") {
                const result = getDynamicValue(thenValue);
                // console.log(`Returning then value: ${result}`);
                return result;
            } else {
                // console.log('Condition not met, returning null');
                return null;
            }
        };

        const evaluateExpression = (expressionArray) => {
            let localResult = 1; // Initialize to 1 for multiplication
            let localOperator = "*"; // Start with multiplication

            for (let j = 0; j < expressionArray.length; j++) {
                let item = expressionArray[j];

                // Handle multi-word dynamic values
                if (item === "Invoice" && expressionArray[j + 1] === "Value") {
                    item = "Invoice Value";
                    j++; // Skip the next item
                }
                if (item === "Shipment" && expressionArray[j + 1] === "Value") {
                    item = "Invoice Value"; // Map to Invoice Value which is totalItemsValue
                    j++; // Skip the next item
                }
                if (item === "Item" && expressionArray[j + 1] === "Weight") {
                    item = "Item Weight";
                    j++; // Skip the next item
                }
                if (item === "Quantity" && expressionArray[j + 1] === "Quantity") {
                    item = "Quantity";
                    j++;
                }
                if (item === "Total Item Quantity" && expressionArray[j + 1] === "Quantity") {
                    item = "Total Item Quantity";
                    j++;
                }
                if (item === "Volume" && expressionArray[j + 1] === "Weight") {
                    item = "Volume Weight";
                    j++;
                }
                if (item === "Airfreight" && expressionArray[j + 1] === "Volume") {
                    item = "Airfreight Volume";
                    j++;
                }
                if (item === "Airfreight" && expressionArray[j + 1] === "Charges") {
                    item = "Airfreight Charges";
                    j++;
                }
                if (item === "CIF" && expressionArray[j + 1] === "CIF") {
                    item = "CIF";
                    j++; // Skip the next item
                }
                if (item === "CIF110" && expressionArray[j + 1] === "CIF110") {
                    item = "CIF110";
                    j++; // Skip the next item
                }
                if (item === "Insurance" && expressionArray[j + 1] === "Insurance") {
                    item = "Insurance";
                    j++; // Skip the next item
                }
                if (item === "HSCode" && expressionArray[j + 1] === "Count") {
                    item = "HSCode Count";
                    j++; // Skip the next item
                }
                if (item === "Administration" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                    item = "Administration A Class";
                    j += 2;
                }
                if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "A" && expressionArray[j + 3] === "Class") {
                    item = "Customs Clearance A Class";
                    j += 3;
                }
                if (item === "Handling" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                    item = "Handling A Class";
                    j += 2;
                }
                if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "B" && expressionArray[j + 3] === "Class") {
                    item = "Customs Clearance B Class";
                    j += 3;
                }
                if (item === "THC" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                    item = "THC B Class";
                    j += 2;
                }
                if (item === "THC" && expressionArray[j + 1] === "A" && expressionArray[j + 2] === "Class") {
                    item = "THC A Class";
                    j += 2;
                }
                if (item === "Handling" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                    item = "Handling B Class";
                    j += 2;
                }
                if (item === "Administration" && expressionArray[j + 1] === "B" && expressionArray[j + 2] === "Class") {
                    item = "Administration B Class";
                    j += 2;
                }
                if (item === "Customs" && expressionArray[j + 1] === "Clearance" && expressionArray[j + 2] === "C" && expressionArray[j + 3] === "Class") {
                    item = "Customs Clearance C Class";
                    j += 3;
                }
                if (item === "Handling" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                    item = "Handling C Class";
                    j += 2;
                }
                if (item === "Administration" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                    item = "Administration C Class";
                    j += 2;
                }
                if (item === "THC" && expressionArray[j + 1] === "C" && expressionArray[j + 2] === "Class") {
                    item = "THC C Class";
                    j += 2;
                }

                if (item === "," || item === "(" || item === ")") continue;

                if (["*", "/", "+", "-", "%"].includes(item)) {
                    localOperator = item;
                } else {
                    const value = getDynamicValue(item);
                    // console.log(`Item: ${item}, Value: ${value}, Operator: ${localOperator}`); // Debugging log
                    switch (localOperator) {
                        case "+": localResult += value; break;
                        case "-": localResult -= value; break;
                        case "*": localResult *= value; break;
                        case "/": localResult /= value !== 0 ? value : 1; break;
                        case "%":
                            if (localResult === 1) {
                                // For percentage calculations, use the first value as the base
                                localResult = (value * localResult) / 100;
                            } else {
                                localResult = (localResult * value) / 100;
                            }
                            break;
                    }
                }
            }
            // console.log(`Expression result: ${localResult}`); // Debugging log
            return localResult;
        };

        const evaluateHSCodeCondition = (condition, count) => {
            const [operator, value] = condition.split(" ");
            switch (operator) {
                case ">":
                    return count > Number(value);
                case "<":
                    return count < Number(value);
                case ">=":
                    return count >= Number(value);
                case "<=":
                    return count <= Number(value);
                case "==":
                    return count === Number(value);
                default:
                    return false;
            }
        };

        for (let i = 0; i < service.values.length; i++) {
            const item = service.values[i];

            // console.log(`Processing item at index ${i}: ${item}`);

            if (item === "if") {
                const complexResult = evaluateComplexCondition(service.values.slice(i));
                if (complexResult !== null) {
                    return complexResult;
                }
            }

            if (item === "min" || item === "max") {
                // console.log(`Processing ${item} expression`); // Debugging log
                const expressionStart = service.values.indexOf("(", i);
                const expressionEnd = service.values.lastIndexOf(")");
                if (expressionStart !== -1 && expressionEnd !== -1) {
                    const expressionArray = service.values.slice(expressionStart + 1, expressionEnd);
                    const expressions = expressionArray.join(' ').split(',').map(expr => expr.trim());
                    // console.log(`Expressions to evaluate: ${JSON.stringify(expressions)}`); // Debugging log
                    const results = expressions.map(expr => evaluateExpression(expr.split(' ')));
                    // console.log(`Results: ${JSON.stringify(results)}`); // Debugging log
                    result = item === "min"
                        ? Math.min(...results)
                        : Math.max(...results);
                    return Number(result.toFixed(2)); // Round to 2 decimal places
                }
            }

            if (service.values.includes('(')) {
                return evaluateComplexFormula(service.values, values, calculatedValues);
            }


            if (item === "if") {
                // const ifResult = evaluateIfCondition(service.values, i);
                // if (ifResult !== null) {
                //   return ifResult;
                // }

                const condition = service.values[i + 1];
                const value = service.values[i + 2];
                const keyword = service.values[i + 3];

                if (HS_CODE_STARTS_WITH_KEYS.includes(condition)) {
                    // Extract prefixes until 'then', handling both single and multiple cases
                    const prefixes = [];
                    let j = i + 2;

                    // Keep collecting prefixes until we hit 'then'
                    while (j < service.values.length && service.values[j] !== 'then') {
                        // Only add non-comma values
                        if (service.values[j] !== ',') {
                            prefixes.push(service.values[j]);
                        }
                        j++;
                    }

                    // console.log('Collected HSCode prefixes:', prefixes);
                    const hsCodeMatches = checkHSCodeStartsWith(values.items, prefixes);
                    // console.log('HSCode match result:', hsCodeMatches);

                    if (hsCodeMatches) {
                        // Find the 'then' index and process the formula
                        const thenIndex = service.values.indexOf('then', i);
                        const elseIndex = service.values.indexOf('else', thenIndex);
                        const formulaEndIndex = elseIndex !== -1 ? elseIndex : service.values.length;

                        // console.log('Processing then clause, formula slice:', service.values.slice(thenIndex + 1, formulaEndIndex));
                        const subFormula = {
                            values: service.values.slice(thenIndex + 1, formulaEndIndex),
                        };

                        return calculateServiceValue(subFormula, values, calculatedValues);
                    } else if (service.values.includes('else')) {
                        // console.log('Processing else clause');
                        const elseIndex = service.values.indexOf('else');
                        const subFormula = {
                            values: service.values.slice(elseIndex + 1),
                        };

                        return calculateServiceValue(subFormula, values, calculatedValues);
                    }

                    return 0;
                }

                if (condition === "document") {
                    const isDocRequired = values.requiredDoc === 'Required' ||
                        values.importDocsRequired === 'Required' ||
                        importDocsRequired === 'Required';

                    const expectedValue = value === 'Required';
                    const isMet = isDocRequired === expectedValue;

                    if (isMet) {
                        const thenIndex = service.values.indexOf('then', i);
                        const elseIndex = service.values.indexOf('else', thenIndex);
                        const formulaEndIndex = elseIndex !== -1 ? elseIndex : service.values.length;

                        const subFormula = {
                            values: service.values.slice(thenIndex + 1, formulaEndIndex),
                        };

                        return calculateServiceValue(subFormula, values, calculatedValues);
                    } else if (service.values.includes('else')) {
                        const elseIndex = service.values.indexOf('else');
                        const subFormula = {
                            values: service.values.slice(elseIndex + 1),
                        };

                        return calculateServiceValue(subFormula, values, calculatedValues);
                    }
                    return 0;
                }
            }

            if (item === "HSCode Count") {
                const condition = service.values[i + 1] + " " + service.values[i + 2];
                const keyword = service.values[i + 3];
                const hsCodeCount = getUniqueHSCodeCount(values.items);

                // console.log("Processing HSCode Count:", {
                //   condition,
                //   keyword,
                //   hsCodeCount,
                //   values: service.values,
                //   index: i
                // });

                if (evaluateHSCodeCondition(condition, hsCodeCount)) {
                    // console.log("Condition met for HSCode Count:", condition);
                    // If condition matches, use the formula after the keyword
                    const formulaStartIndex = i + 4;
                    const elseIndex = service.values.indexOf("else", formulaStartIndex);
                    const formulaEndIndex =
                        elseIndex !== -1 ? elseIndex : service.values.length;

                    // console.log("HSCode Count formula:", service.values.slice(formulaStartIndex, formulaEndIndex));

                    const subFormula = {
                        values: service.values.slice(formulaStartIndex, formulaEndIndex),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                } else if (service.values.includes("else")) {
                    // console.log("Condition not met, executing else clause");
                    // If condition doesn't match and there's an else clause, use the else part
                    const elseIndex = service.values.indexOf("else");
                    const subFormula = {
                        values: service.values.slice(elseIndex + 1),
                    };

                    return calculateServiceValue(subFormula, values, calculatedValues);
                }
                // console.log("No condition met and no else clause, returning 0");
                // If no condition is met and no else clause, return 0
                return 0;
            }

            if (item === "COO") {
                // Handle COO logic
                const country = service.values[i + 1];
                const keyword = service.values[i + 2];
                const matchingItem = values.items.find(
                    (item) => item.countryOrigin === country
                );

                if (matchingItem) {
                    // If country matches, use the formula after the keyword
                    const formulaStartIndex = i + 3;
                    const elseIndex = service.values.indexOf("else", formulaStartIndex);
                    const formulaEndIndex =
                        elseIndex !== -1 ? elseIndex : service.values.length;

                    const subFormula = {
                        values: service.values.slice(formulaStartIndex, formulaEndIndex),
                    };

                    result = calculateServiceValue(subFormula, values, calculatedValues);
                } else if (service.values.includes("else")) {
                    // If country doesn't match and there's an else clause, use the else part
                    const elseIndex = service.values.indexOf("else");
                    const subFormula = {
                        values: service.values.slice(elseIndex + 1),
                    };

                    result = calculateServiceValue(subFormula, values, calculatedValues);
                }

                // Skip to the end of the COO block
                return result;
            }

            if (i % 2 === 0) {
                // It's a value or variable
                let numValue;
                // console.log(`Calculating value for: ${item}`);

                switch (item) {
                    case "Invoice Value":
                    case "Shipment Value":
                        numValue = totalItemsValue;
                        break;
                    case "Item Weight":
                        numValue = totalDimensionsWeight;
                        break;
                    case "Volume Weight":
                        const totalVolumetricWeight = calculateTotalVolumetricWeight(values.dimensions);
                        // console.log(totalVolumetricWeight, 'totalVolumetricWeight');
                        numValue = Math.max(totalDimensionsWeight, totalVolumetricWeight);
                        break;
                    case "Airfreight Volume":
                        numValue = calculateTotalVolumetricWeight(values.dimensions);
                        break;
                    case "Airfreight Charges":
                        const chargeableWeight = calculateTotalVolumetricWeight(values.dimensions);
                        const volumeWeight = Math.max(totalDimensionsWeight, chargeableWeight);
                        if (volumeWeight < 360) {
                            numValue = 6.50 * volumeWeight + 250;
                            break;
                        } else if (volumeWeight >= 360 && volumeWeight <= 1080) {
                            numValue = 5.00 * volumeWeight + 250;
                            break;
                        } else {
                            numValue = 4.00 * volumeWeight + 250;
                            break;
                        }
                    case "CIF":
                        numValue = cif;
                        break;
                    case "CIF110":
                        numValue = totalItemsValue * 1.10;
                        break;
                    case "Insurance":
                        numValue = insurance;
                        break;
                    case "Quantity":
                        numValue = totalItemsQuantity;
                        break;
                    case "Total Item Quantity":
                        numValue = itemsTotalQty;
                        break;
                    case "HSCode Count":
                        numValue = getUniqueHSCodeCount(values.items);
                        break;
                    case "IOR":
                        const isDocRequired = values.requiredDoc === 'Required' || values.importDocsRequired === 'Required' || importDocsRequired === 'Required';
                        if (isDocRequired) numValue = Math.max((totalItemsValue * 7.5) / 100, 950);
                        else numValue = Math.max((totalItemsValue * 5) / 100, 650);
                        break;
                    case "Handling Charges":
                        const totalVolWeightHC = calculateTotalVolumetricWeight(values.dimensions);
                        const chargeableWeightHC = Math.max(totalDimensionsWeight, totalVolWeightHC);
                        numValue = chargeableWeightHC >= 360 ? 250 : 200;
                        break;
                    case "Administration":
                        if (totalItemsValue < 800) numValue = 150;
                        else if (totalItemsValue <= 10000) numValue = 200;
                        else numValue = 250;
                        break;
                    case "Customs Clearance A Class":
                        if (totalItemsValue < 250) numValue = 250;
                        else if (totalItemsValue < 2500) numValue = 350;
                        else if (totalItemsValue < 100000) numValue = 450;
                        else numValue = 550;
                        break;
                    case "Handling A Class":
                        if (totalItemsValue < 250) numValue = 75;
                        else if (totalItemsValue < 1000) numValue = 150;
                        else if (totalItemsValue < 2500) numValue = 250;
                        else if (totalItemsValue < 50000) numValue = 300;
                        else if (totalItemsValue < 500000) numValue = 350;
                        else numValue = 450;
                        break;
                    case "THC A Class":
                        if (totalItemsValue < 1000) numValue = 150;
                        else if (totalItemsValue < 2500) numValue = 195;
                        else if (totalItemsValue < 50000) numValue = 350;
                        else if (totalItemsValue < 100000) numValue = 400;
                        else if (totalItemsValue < 500000) numValue = 450;
                        else numValue = 575;
                        break;
                    case "Administration A Class":
                        if (totalItemsValue < 250) numValue = 50;
                        else if (totalItemsValue < 1000) numValue = 100;
                        else if (totalItemsValue < 2500) numValue = 150;
                        else if (totalItemsValue < 500000) numValue = 250;
                        else numValue = 300;
                        break;
                    case "Customs Clearance B Class":
                        if (totalItemsValue < 250) numValue = 250;
                        else if (totalItemsValue < 1000) numValue = 350;
                        else if (totalItemsValue < 2500) numValue = 450;
                        else if (totalItemsValue < 100000) numValue = 550;
                        else numValue = 650;
                        break;
                    case "THC B Class":
                        if (totalItemsValue < 1000) numValue = 195;
                        else if (totalItemsValue < 2500) numValue = 250;
                        else if (totalItemsValue < 50000) numValue = 395;
                        else if (totalItemsValue < 500000) numValue = 450;
                        else numValue = 550;
                        break;
                    case "Handling B Class":
                        if (totalItemsValue < 250) numValue = 75;
                        else if (totalItemsValue < 1000) numValue = 150;
                        else if (totalItemsValue < 50000) numValue = 250;
                        else numValue = 350;
                        break;
                    case "Administration B Class":
                        if (totalItemsValue < 250) numValue = 50;
                        else if (totalItemsValue < 1000) numValue = 100;
                        else if (totalItemsValue < 50000) numValue = 200;
                        else numValue = 250;
                        break;
                    case "Customs Clearance C Class":
                        if (totalItemsValue < 250) numValue = 350;
                        else if (totalItemsValue < 2500) numValue = 450;
                        else if (totalItemsValue < 100000) numValue = 550;
                        else numValue = 750;
                        break;
                    case "Handling C Class":
                        if (totalItemsValue < 250) numValue = 75;
                        else if (totalItemsValue < 1000) numValue = 200;
                        else if (totalItemsValue < 50000) numValue = 250;
                        else if (totalItemsValue < 100000) numValue = 350;
                        else numValue = 550;
                        break;
                    case "Administration C Class":
                        if (totalItemsValue < 250) numValue = 50;
                        else if (totalItemsValue < 1000) numValue = 150;
                        else if (totalItemsValue < 50000) numValue = 200;
                        else numValue = 250;
                        break;
                    case "THC C Class":
                        if (totalItemsValue < 1000) numValue = 150;
                        else if (totalItemsValue < 2500) numValue = 195;
                        else if (totalItemsValue < 50000) numValue = 350;
                        else if (totalItemsValue < 100000) numValue = 400;
                        else if (totalItemsValue < 500000) numValue = 450;
                        else numValue = 575;
                        break;

                    default:
                        // Check if it's a reference to another service
                        if (calculatedValues[item]) {
                            numValue = calculatedValues[item];
                        } else {
                            numValue = parseFloat(item) || 0;
                        }
                        break;
                }

                // console.log(`Calculated numValue for ${item}: ${numValue}`);

                switch (operator) {
                    case "+":
                        result += numValue;
                        break;
                    case "-":
                        result -= numValue;
                        break;
                    case "*":
                        result *= numValue;
                        break;
                    case "/":
                        result /= numValue !== 0 ? numValue : 1; // Avoid division by zero
                        break;
                    case ">":
                        lastValue = numValue;
                        break;
                    case "%":
                        result = (result * numValue) / 100;
                        break;
                }
                // console.log(`After ${operator} operation, result: ${result}`);
            } else {
                // It's an operator
                operator = item;
                // console.log(`Set new operator: ${operator}`);
            }
        }

        if (operator === ">" && result < lastValue) {
            // console.log(`Applying > condition: setting result to ${lastValue}`);
            result = lastValue;
        }

        // console.log(`Final result for service calculation: ${result}`);
        return result;
    };

    const handleGetEstimation = async () => {
        const isValid = validateFields();
        if (!isValid) {
            message.error({
                content: "Please fill in all fields before getting an estimation.",
                style: {
                    marginTop: '20vh',
                },
                duration: 1,
            });
            return;
        }

        const isDutyVatDisabled = disabledDutyVatCountries.some(country =>
            country.countryName.toLowerCase() === formData.unloadCountry.toLowerCase()
        );

        // Check if the current template is ONLY an EOR template (not EOR & IOR)
        const isEOROnlyTemplate = formData.selectedTemplate?.name?.toLowerCase() === 'eor';

        let dhlData = null;

        // Skip DHL API call if it's an EOR-only template
        if (isEOROnlyTemplate) {
            message.info('Duty & VAT estimation skipped for EOR template');
            // Set duty to 0 for EOR templates since we're not calling DHL
            setDuty(0);
        } else if (!isDutyVatDisabled) {
            // Only call DHL API for non-EOR-only templates and enabled countries
            dhlData = await getDHLEstimation(localFormData.items);

            if (dhlData) {
                message.success({
                    content: 'DHL estimation received',
                    key: 'dhlEstimation',
                    duration: 2
                });
                const dutyValue = dhlData.feeTotals.find(fee => fee.name === 'DUTY')?.value || 0;
                setDuty(dutyValue);
            } else {
                // If DHL call failed, ensure duty is 0
                setDuty(0);
            }
        } else {
            // For disabled duty/VAT countries (non-EOR)
            message.info(`Duty & VAT estimations are not available for ${formData.unloadCountry}. Calculations will proceed without them.`);
            setDuty(0);
        }

        const calculatedInsurance = needInsurance === 'YES' ? totalItemsValue * 0.0075 : 0;
        setInsurance(calculatedInsurance);

        // Trigger estimation summary calculation regardless of DHL call
        setIsDHLDataReceived(true);
    };

    // useEffect to calculate estimationSummary after DHL data is received and state is updated
    useEffect(() => {
        if (isDHLDataReceived && localFormData.selectedTemplate && localFormData.selectedTemplate.services) {
            const newCalculatedValues = {};
            localFormData.selectedTemplate.services.forEach((service) => {
                if (service.description === 'Insurance' && needInsurance === 'NO') {
                    return; // Skip insurance calculation if not needed
                }
                newCalculatedValues[service.description] = calculateServiceValue(
                    service,
                    { ...localFormData, insuranceSelected: needInsurance },
                    newCalculatedValues
                );
            });

            const collectionService = localFormData.selectedTemplate.services.find(
                service => service.description.toUpperCase() === 'COLLECTION'
            );
            if (collectionService) {
                setCollectionValue(newCalculatedValues['COLLECTION'] || 0);
            } else {
                setCollectionValue(0);
            }

            // Calculate total
            const total = Object.values(newCalculatedValues).reduce((sum, value) => sum + value, 0);
            newCalculatedValues["Total"] = total;

            // console.log('Estimation Summary:', newCalculatedValues);

            setEstimationSummary(newCalculatedValues);

            // Calculate CIF with the latest duty and collectionValue
            const calculatedCIF = totalItemsValue + airfreight + insurance + duty + (newCalculatedValues['COLLECTION'] || 0);
            setCif(calculatedCIF);

            setIsEstimationComplete(true);
            setIsDHLDataReceived(false); // Reset the flag
        }
    }, [isDHLDataReceived, localFormData, needInsurance, calculateServiceValue, totalItemsValue, airfreight, insurance, duty]);

    // useEffect to recalculate estimation when importDocsRequired changes after estimation is complete
    useEffect(() => {
        if (isEstimationComplete && localFormData.selectedTemplate && localFormData.selectedTemplate.services) {
            const newCalculatedValues = {};
            localFormData.selectedTemplate.services.forEach((service) => {
                if (service.description === 'Insurance' && needInsurance === 'NO') {
                    return; // Skip insurance calculation if not needed
                }
                newCalculatedValues[service.description] = calculateServiceValue(
                    service,
                    { ...localFormData, insuranceSelected: needInsurance },
                    newCalculatedValues
                );
            });

            const collectionService = localFormData.selectedTemplate.services.find(
                service => service.description.toUpperCase() === 'COLLECTION'
            );
            if (collectionService) {
                setCollectionValue(newCalculatedValues['COLLECTION'] || 0);
            } else {
                setCollectionValue(0);
            }

            // Calculate total
            const total = Object.values(newCalculatedValues).reduce((sum, value) => sum + value, 0);
            newCalculatedValues["Total"] = total;

            setEstimationSummary(newCalculatedValues);

            // Calculate CIF with the latest duty and collectionValue
            const calculatedCIF = totalItemsValue + airfreight + insurance + duty + (newCalculatedValues['COLLECTION'] || 0);
            setCif(calculatedCIF);
        }
    }, [importDocsRequired, isEstimationComplete, localFormData, needInsurance, calculateServiceValue, totalItemsValue, airfreight, insurance, duty]);

    const validateFields = () => {
        if (!totalDimensionsWeight || !totalDimensionsLength || !totalDimensionsWidth || !totalDimensionsHeight) {
            return false;
        }

        // Validate dimensions quantity
        const areDimensionsQuantitiesValid = localFormData.dimensions.every(dimension =>
            dimension.quantity && parseInt(dimension.quantity, 10) > 0
        );

        if (!areDimensionsQuantitiesValid) {
            return false;
        }

        // Validate state/province selection
        if (needsStateSelection(formData.loadCountry) && !senderState) {
            message.error('Sender state/province is required for Canada and Brazil');
            return false;
        }
        if (needsStateSelection(formData.unloadCountry) && !receiverState) {
            message.error('Receiver state/province is required for Canada and Brazil');
            return false;
        }

        for (const item of localFormData.items) {
            if (!item.name || !item.quantity || !item.itemValue || !item.countryOrigin || !item.hsCode) {
                return false;
            }
        }

        if (!importDocsRequired) {
            return false;
        }

        return true;
    };

    const handleChange = (e, index) => {
        const { name, value } = e.target;
        handleItemChange(index, name, value);
    };

    const handleClose = () => {
        // Reset all state when modal closes
        resetAllState();
        onClose();
    };

    // Helper function to safely get country code from either string or array input 
    const getCountryCode = (countryInput) => {
        if (!countryInput) return null;

        // Handle array input
        if (Array.isArray(countryInput)) {
            if (countryInput.length === 0) return null;
            return getAlpha2Code(countryInput[0]);
        }

        // Handle string input
        if (typeof countryInput === 'string') {
            return getAlpha2Code(countryInput);
        }

        return null;
    };

    // Function to get DHL access token
    const getDHLToken = async () => {
        try {
            const response = await fetch('https://api.dhl.com/auth/v1/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    grant_type: 'client_credentials',
                    response_type: 'access_token',
                    client_id: '7E2HCWQVR0A10NJKYbYnxRt5uFT7vn1z',
                    client_secret: 'uB26UUKHmBMJqbMQ'
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get access token');
            }

            const data = await response.json();
            setAccessToken(data.access_token);
            // Set token expiry to 15 minutes from now
            setTokenExpiry(Date.now() + (15 * 60 * 1000));
            return data.access_token;
        } catch (error) {
            console.error('Error getting DHL token:', error);
            message.error('Failed to authenticate with DHL');
            return null;
        }
    };

    // Function to check and refresh token if needed
    const ensureValidToken = useCallback(async () => {
        if (!accessToken || !tokenExpiry || Date.now() >= tokenExpiry) {
            return await getDHLToken();
        }
        return accessToken;
    }, [accessToken, tokenExpiry]);

    // Calculate total weight from dimensions
    const calculateTotalWeight = (dimensions) => {
        return dimensions.reduce((total, dim) => {
            const weight = parseFloat(dim.weight.value) || 0;
            const quantity = parseInt(dim.quantity) || 0;
            return total + (weight * quantity);
        }, 0);
    };

    // Function to check if a country needs state/province selection
    const needsStateSelection = (country) => {
        const countryCode = getCountryCode(country);
        return countryCode === 'CA' || countryCode === 'BR';
    };

    // Get available states based on country
    const getAvailableStates = (country) => {
        const countryCode = getCountryCode(country);
        if (countryCode === 'CA') return CANADIAN_PROVINCES;
        if (countryCode === 'BR') return BRAZILIAN_STATES;
        return [];
    };

    // Function to prepare DHL API request body with dynamic values
    const prepareDHLRequestBody = (formItems, dimensions) => {
        const senderCountry = getCountryCode(formData.loadCountry) || '';
        const receiverCountry = getCountryCode(formData.unloadCountry) || '';
        const currency = formData.airfreightCurrency;

        const requestBody = {
            pricingStrategy: pricingStrategy,
            senderAddress: {
                country: senderCountry
            },
            consigneeAddress: {
                country: receiverCountry
            },
            packageDetails: {
                outputCurrency: currency,
                transportMode: transportMode,
                endUse: "BUSINESS",
                clearanceMode: "COURIER",
                tax: {
                    "included": false,
                    "percent": 0,
                    "return": true
                },
                freightCharge: {
                    value: /*parseFloat(airfreight) ||*/ 0,
                    currency: currency
                },
                insuranceCharge: {
                    value: /*needInsurance === 'YES' ? parseFloat(totalItemsValue * 0.0075) || 0 :*/ 0,
                    currency: currency
                }
            },
            customsDetails: formItems.map(item => ({
                itemId: `ITEM_${Math.random().toString(36).substr(2, 9)}`,
                itemDescription: item.name,
                hsCode: item.hsCode,
                countryOfOrigin: getAlpha2Code(item.countryOrigin),
                itemQuantity: {
                    value: parseInt(item.quantity),
                    unit: "PCS"
                },
                itemValue: {
                    value: (parseFloat(item.itemValue) * 1.1),
                    currency: currency
                },
                weight: {
                    value: totalDimensionsWeight,
                    unit: dimensions[0].weight.unit.toUpperCase()
                },
            }))
        };

        // Add state/province if required
        if (needsStateSelection(formData.loadCountry)) {
            requestBody.senderAddress.state = senderState;
        }
        if (needsStateSelection(formData.unloadCountry)) {
            requestBody.consigneeAddress.state = receiverState;
        }

        return requestBody;
    };

    // Function to call DHL API
    const getDHLEstimation = async (items) => {
        const token = await ensureValidToken();
        if (!token) return null;

        try {
            const requestBody = prepareDHLRequestBody(items, localFormData.dimensions);
            // console.log('DHL API Request:', requestBody); // For debugging

            const response = await fetch('https://api.dhl.com/dtc/v1/quotes', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error('DHL API Error:', errorData);

                // Check for specific error and retry with MAXIMUM pricing strategy
                if (errorData.type?.includes('400.0904002')) {
                    setPricingStrategy('MAXIMUM');
                    // Retry the request with new pricing strategy
                    const retryBody = { ...requestBody, pricingStrategy: 'MAXIMUM' };
                    const retryResponse = await fetch('https://api.dhl.com/dtc/v1/quotes', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(retryBody)
                    });

                    if (!retryResponse.ok) {
                        throw new Error('Failed to get estimation even with MAXIMUM pricing strategy');
                    }

                    const retryData = await retryResponse.json();
                    setDhlEstimation(retryData);
                    return retryData;
                }

                throw new Error(errorData.message);
            }

            const data = await response.json();
            setDhlEstimation(data);
            return data;
        } catch (error) {
            console.error('Error getting estimation:', error);
            return null;
        }
    };

    // Add state/province selection fields to the render method
    const renderStateSelection = () => {
        const senderNeedsState = needsStateSelection(formData.loadCountry);
        const receiverNeedsState = needsStateSelection(formData.unloadCountry);

        if (!senderNeedsState && !receiverNeedsState) return null;

        return (
            <div className="grid grid-cols-2 gap-4 mb-6">
                {senderNeedsState && (
                    <div>
                        <label className="block text-sm font-normal mb-1">
                            Sender State/Province <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={senderState}
                            onChange={setSenderState}
                            className="w-full"
                            required
                        >
                            {getAvailableStates(formData.loadCountry).map(({ state, code }) => (
                                <Option key={code} value={code}>{state}</Option>
                            ))}
                        </Select>
                    </div>
                )}
                {receiverNeedsState && (
                    <div>
                        <label className="block text-sm font-normal mb-1">
                            Receiver State/Province <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={receiverState}
                            onChange={setReceiverState}
                            className="w-full"
                            required
                        >
                            {getAvailableStates(formData.unloadCountry).map(({ state, code }) => (
                                <Option key={code} value={code}>{state}</Option>
                            ))}
                        </Select>
                    </div>
                )}
            </div>
        );
    };


    // Render the DHL estimation summary
    const renderDHLSummary = () => {
        if (!dhlEstimation) return null;

        return <DhlSummaryTable dhlEstimation={dhlEstimation} cif={cif} totalItemsValue={totalItemsValue} />;

    };

    // console.log("localFormData localFormData:", localFormData);
    // console.log("totalItemsQuantity", totalItemsQuantity);


    return (
        <Modal
            title="Test Estimation"
            visible={isVisible}
            onCancel={handleClose}
            footer={null}
            width={800}
            className="test-popup"
        >
            {/* Dimensions Section */}
            <div className="space-y-4 mb-6">
                <h2 className="text-lg font-semibold mb-2">Dimensions</h2>
                {localFormData.dimensions.map((dimension, index) => {
                    // Ensure default values for all properties
                    const dimensionData = {
                        weight: { value: '', unit: 'kg', ...dimension.weight },
                        length: { value: '', unit: 'cm', ...dimension.length },
                        width: { value: '', unit: 'cm', ...dimension.width },
                        height: { value: '', unit: 'cm', ...dimension.height },
                        quantity: dimension.quantity
                    };

                    return (
                        <div key={index} className="relative border p-4 rounded-md">
                            <div className="flex gap-6">
                                {/* Weight Field */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        Weight <span className="text-red-500">*</span>
                                    </label>
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="number"
                                            className="w-20 p-2 border rounded-md"
                                            name={`weight-value-${index}`}
                                            value={dimensionData.weight.value}
                                            onChange={(e) => handleDimensionChange(index, 'weight', e)}
                                            min="0"
                                            step="0.01"
                                        />
                                        <select
                                            className="p-2 border rounded-md"
                                            name={`weight-unit-${index}`}
                                            value={dimensionData.weight.unit}
                                            onChange={(e) => handleDimensionChange(index, 'weight', e)}
                                        >
                                            <option value="kg">kg</option>
                                            <option value="lbs">lbs</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Dimensions Fields */}
                                <div className="flex gap-4">
                                    {['length', 'width', 'height'].map((dim) => (
                                        <div key={dim} className="flex flex-col gap-2">
                                            <label className="text-sm font-medium">
                                                {dim.charAt(0).toUpperCase() + dim.slice(1)} <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center space-x-2">
                                                <input
                                                    type="number"
                                                    className="w-20 p-2 border rounded-md"
                                                    name={`${dim}-value-${index}`}
                                                    value={dimensionData[dim].value}
                                                    onChange={(e) => handleDimensionChange(index, dim, e)}
                                                    min="0"
                                                    step="0.01"
                                                />
                                                {dim === "height" && (
                                                    <select
                                                        className="p-2 border rounded-md"
                                                        name={`${dim}-unit-${index}`}
                                                        value={dimensionData[dim].unit}
                                                        onChange={(e) => handleDimensionChange(index, dim, e)}
                                                    >
                                                        <option value="cm">cm</option>
                                                        <option value="in">in</option>
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Quantity Field */}
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-medium">
                                        Quantity <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        className="w-20 p-2 border rounded-md"
                                        value={dimensionData.quantity}
                                        onChange={(e) => handleDimensionChange(index, 'quantity', e)}
                                        min="1"
                                        step="1"
                                    />
                                </div>
                            </div>

                            <div className="absolute right-5 top-2 flex space-x-2">
                                {index === localFormData.dimensions.length - 1 && (
                                    <Button
                                        type="primary"
                                        onClick={handleAddDimension}
                                        className="flex items-center justify-center p-2"
                                    >
                                        <FaCirclePlus />
                                    </Button>
                                )}
                                {localFormData.dimensions.length > 1 && (
                                    <Button
                                        danger
                                        onClick={() => handleRemoveDimension(index)}
                                        className="flex items-center justify-center p-2"
                                    >
                                        <FaCircleMinus />
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            {renderStateSelection()}
            <div className="mb-6">
                <div className="w-full flex gap-5 items-center mt-8 mb-4">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-gray-800">Item Details</h2>
                        <FaCirclePlus
                            className="text-[22px] text-[#03336e] cursor-pointer hover:text-[#044593] transition-colors duration-300"
                            onClick={handleAddItem}
                        />
                    </div>
                </div>
                {localFormData.items.map((item, index) => (
                    <div key={index} className="bg-gray-50 p-4 mb-4 rounded-lg shadow-sm">
                        <h2 className="flex gap-3 items-center text-[16px] font-bold text-gray-600 mb-3">
                            Item {index + 1}
                            {index > 0 && (
                                <FaCircleMinus
                                    className="text-[18px] text-red-500 cursor-pointer hover:text-red-600 transition-colors duration-300"
                                    onClick={() => handleRemoveItem(index)}
                                />
                            )}
                        </h2>
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-3">
                            <div className="flex flex-col gap-1 py-2">
                                <label className="w-full text-sm font-normal">
                                    <span className="text-red-500">*</span> Item Name
                                </label>
                                <Input
                                    value={item.name}
                                    onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex flex-col gap-1 py-2">
                                <label className="w-full text-sm font-normal">
                                    <span className="text-red-500">*</span> Quantity
                                </label>
                                <Input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex flex-col gap-1 py-2">
                                <label className="w-full text-sm font-normal">
                                    <span className="text-red-500">*</span> Item Value
                                </label>
                                <Input
                                    type="number"
                                    value={item.itemValue}
                                    onChange={(e) => handleItemChange(index, 'itemValue', e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>
                            <div className="flex flex-col gap-1 py-2">
                                <label className="w-full text-sm font-normal">
                                    <span className="text-red-500">*</span> Country of Origin
                                </label>
                                <CountrySelectInput
                                    name="countryOrigin"
                                    id={`countryOrigin-${index}`}
                                    placeholder="Select Countries"
                                    value={item.countryOrigin}
                                    onChange={(e) => handleChange(e, index)}
                                />
                            </div>
                            <div className="flex flex-col gap-1 py-2">
                                <label className="w-full text-sm font-normal">
                                    <span className="text-red-500">*</span> HS Code
                                </label>
                                <Input
                                    value={item.hsCode}
                                    onChange={(e) => {
                                        const inputValue = e.target.value.replace(/[^0-9]/g, '');
                                        handleItemChange(index, 'hsCode', inputValue);
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter HS Code (numbers only)"
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex flex-col mt-6 mb-6">
                <div className="flex items-center mb-2">
                    <p className="mr-4 text-sm font-normal"><span className="text-red-500">*</span> Do you need insurance?</p>
                    <div className="flex items-center">
                        <label className="inline-flex items-center mr-4">
                            <input
                                type="radio"
                                className="form-radio h-4 w-4 text-blue-600"
                                name="insurance"
                                value="YES"
                                checked={needInsurance === 'YES'}
                                onChange={() => setNeedInsurance('YES')}
                            />
                            <span className="ml-2">Yes</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                className="form-radio h-4 w-4 text-blue-600"
                                name="insurance"
                                value="NO"
                                checked={needInsurance === 'NO'}
                                onChange={() => setNeedInsurance('NO')}
                            />
                            <span className="ml-2">No</span>
                        </label>
                    </div>
                </div>

                <div className="flex items-center mt-2 mb-2">
                    <p className="mr-4 text-sm font-normal">
                        <span className="text-red-500">*</span> Import documentation required?
                    </p>
                    <div className="flex items-center">
                        <label className="inline-flex items-center mr-4">
                            <input
                                type="radio"
                                className="form-radio h-4 w-4 text-blue-600"
                                name="importDocs"
                                value="Required"
                                checked={importDocsRequired === 'Required'}
                                onChange={() => setImportDocsRequired('Required')}
                            />
                            <span className="ml-2">Required</span>
                        </label>
                        <label className="inline-flex items-center">
                            <input
                                type="radio"
                                className="form-radio h-4 w-4 text-blue-600"
                                name="importDocs"
                                value="Not_Required"
                                checked={importDocsRequired === 'Not_Required'}
                                onChange={() => setImportDocsRequired('Not_Required')}
                            />
                            <span className="ml-2">Not Required</span>
                        </label>
                    </div>
                </div>

                <div className="flex mt-2">
                    <div className="w-1/3">
                        <label className="block text-sm font-normal mb-1"><span className="text-red-500">*</span> Transport Mode</label>
                        <Select
                            value={transportMode}
                            onChange={setTransportMode}
                            className="w-full"
                        >
                            <Option value="AIR">AIR</Option>
                            <Option value="OCEAN">OCEAN</Option>
                            <Option value="GROUND">GROUND</Option>
                        </Select>
                    </div>
                </div>

                {/* New charges section */}
                <div className='flex justify-center mt-6'>
                    <div className="grid grid-cols-3 gap-4">
                        {/* <div>
                <label className="block text-sm font-normal mb-1">Freight Charge</label>
                <Input
                    type="number"
                    value={freightCharge}
                    onChange={(e) => setFreightCharge(e.target.value)}
                    className="w-full"
                />
            </div>

            <div>
                <label className="block text-sm font-normal mb-1">Insurance Charge</label>
                <Input
                    type="number"
                    value={insuranceCharge}
                    onChange={(e) => setInsuranceCharge(e.target.value)}
                    disabled={needInsurance === 'NO'}
                    className="w-full"
                />
            </div> */}
                    </div>
                </div>
            </div>
            <div className="flex justify-center mt-4 mb-4">
                <Button onClick={handleGetEstimation} className="px-8 py-2 flex items-center justify-center">
                    Get Estimation
                </Button>
            </div>

            {(estimationSummary || dhlEstimation) && (
                <div className="mt-8">
                    <h3 className="text-xl font-semibold mb-4">Estimation Summary</h3>

                    {/* Services Section */}
                    {estimationSummary && (
                        <div className="mb-6">
                            <h4 className="text-lg font-medium mb-2">Services</h4>
                            <table className="w-full border-collapse border border-gray-300 mb-2">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border border-gray-300 p-2 text-left">Service</th>
                                        <th className="border border-gray-300 p-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(estimationSummary)
                                        .filter(([service]) => service !== 'Total')
                                        .map(([service, amount]) => (
                                            <tr key={service}>
                                                <td className="border border-gray-300 p-2">{service}</td>
                                                <td className="border border-gray-300 p-2 text-right">
                                                    {amount.toFixed(2)}
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                            <div className="flex justify-end pr-2">
                                <div className="font-bold">
                                    Subtotal: {estimationSummary?.Total?.toFixed(2) || '0.00'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* DHL Duty and Tax Estimation */}
                    {dhlEstimation && (
                        <div className="mb-6">
                            <h4 className="text-lg font-medium mb-2">Duty and Tax Estimation</h4>
                            {renderDHLSummary()}
                        </div>
                    )}

                    {/* Combined Total */}
                    {(estimationSummary || dhlEstimation) && (
                        <div className="flex justify-end border-t pt-2">
                            <div className="font-bold text-lg">
                                Total:{' '}
                                {(
                                    (estimationSummary?.Total || 0) +
                                    (dhlEstimation
                                        ? Object.values({
                                            ...dhlEstimation.feeTotals.reduce((acc, fee) => ({
                                                ...acc,
                                                [fee.name]: fee
                                            }), {}),
                                            // Add any additional taxes from DhlSummaryTable logic
                                        }).reduce((sum, fee) => sum + (fee.value > 0 ? fee.value : 0), 0)
                                        : 0)
                                ).toFixed(2)}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {isEstimationComplete && (
                <div className="flex justify-center mt-4">
                    <Button onClick={onClose} className="px-8 py-2 flex items-center justify-center">
                        Exit
                    </Button>
                </div>
            )}
        </Modal>
    );
};
export default TestPopup;
